import { useEffect, useRef, useState, type RefObject } from 'react';

export const QR_CAMERA_STORAGE_KEY = 'posQrCameraId';

export function useQrCamera(active: boolean, video: RefObject<HTMLVideoElement>, onScan: (text: string) => boolean | Promise<boolean>) {
  const [cameras, setCameras] = useState<{ id: string; label: string }[]>([]);
  const [cameraId, setCameraId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [ready, setReady] = useState(false);
  const callback = useRef(onScan);
  callback.current = onScan;

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    setError(''); setCameraId(null); setCameras([]);
    import('qr-scanner').then(async ({ default: Scanner }) => {
      const found = await Scanner.listCameras(true);
      if (cancelled) return;
      if (!found.length) throw new Error('no camera');
      setCameras(found);
      let saved: string | null = null;
      try { saved = localStorage.getItem(QR_CAMERA_STORAGE_KEY); } catch {}
      if (found.some((camera) => camera.id === saved)) setCameraId(saved);
      else if (found.length === 1) setCameraId(found[0].id);
    }).catch(() => { if (!cancelled) setError('לא ניתן לפתוח מצלמה. יש לאפשר גישה למצלמה במכשיר.'); });
    return () => { cancelled = true; };
  }, [active]);

  useEffect(() => {
    setReady(false);
    if (!active || !cameraId || !video.current) return;
    let cancelled = false;
    let scanner: import('qr-scanner').default | undefined;
    let scanned = false;
    setError('');
    import('qr-scanner').then(async ({ default: Scanner }) => {
      if (cancelled || !video.current) return;
      scanner = new Scanner(video.current, (result) => {
        if (cancelled || scanned) return;
        scanned = true; scanner?.stop();
        Promise.resolve(callback.current(result.data)).then((successful) => {
          if (successful) {
            try { localStorage.setItem(QR_CAMERA_STORAGE_KEY, cameraId); } catch {}
          }
        }).catch(() => {});
      }, { preferredCamera: cameraId, returnDetailedScanResult: true, maxScansPerSecond: 5 });
      await scanner.start();
      if (cancelled) scanner.destroy();
      else setReady(true);
    }).catch(() => { if (!cancelled) setError('לא ניתן להפעיל את המצלמה שנבחרה'); });
    return () => { cancelled = true; scanner?.destroy(); };
  }, [active, cameraId, video]);

  return { cameras, cameraId, setCameraId, error, ready };
}